import { useState, useRef, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { contactLabel } from "../../utils/taskHelpers";

const MultiContactSearchDropdown = ({
  contacts,
  value = [],
  onChange,
  placeholder = "Select team members...",
  itemsPerPage = 10,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [displayedContacts, setDisplayedContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedIds = Array.isArray(value) ? value : [];

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredContacts(contacts);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredContacts(
        contacts.filter(
          (contact) =>
            (contact.Name || "").toLowerCase().includes(term) ||
            (contact.Surname || "").toLowerCase().includes(term) ||
            (contact.email || "").toLowerCase().includes(term)
        )
      );
    }
    setCurrentPage(1);
  }, [searchTerm, contacts]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    setDisplayedContacts(filteredContacts.slice(startIndex, startIndex + itemsPerPage));
  }, [filteredContacts, currentPage, itemsPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [isOpen]);

  const toggleContact = (contactId) => {
    const next = selectedIds.includes(contactId)
      ? selectedIds.filter((id) => id !== contactId)
      : [...selectedIds, contactId];
    onChange(next);
  };

  const removeContact = (contactId) => {
    onChange(selectedIds.filter((id) => id !== contactId));
  };

  const selectedContacts = contacts.filter((c) => selectedIds.includes(c.id));
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-h-[42px] rounded border border-gray-300 bg-white p-2 text-left transition hover:bg-gray-50"
      >
        {selectedContacts.length === 0 ? (
          <span className="text-gray-500">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selectedContacts.map((contact) => (
              <span
                key={contact.id}
                className="inline-flex items-center gap-1 rounded-full bg-[#90c740]/15 px-2 py-0.5 text-xs font-medium text-gray-800"
              >
                {contactLabel(contact)}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeContact(contact.id);
                  }}
                  className="text-gray-500 hover:text-red-500"
                >
                  <FaTimes className="text-[10px]" />
                </button>
              </span>
            ))}
          </div>
        )}
      </button>

      {isOpen && (
        <div className="relative z-50 mt-1 flex max-h-96 w-full flex-col rounded border border-gray-300 bg-white shadow-lg">
          <div className="sticky top-0 border-b border-gray-200 bg-white p-3">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-[#90c740]"
            />
            <p className="mt-2 text-xs text-gray-500">
              {selectedIds.length} selected · {filteredContacts.length} results
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {displayedContacts.length > 0 ? (
              displayedContacts.map((contact) => {
                const checked = selectedIds.includes(contact.id);
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => toggleContact(contact.id)}
                    className={`flex w-full items-start gap-3 border-b border-gray-100 p-3 text-left transition hover:bg-blue-50 ${
                      checked ? "bg-blue-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={checked}
                      className="mt-1 h-4 w-4 rounded text-[#90c740]"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        {contact.Name} {contact.Surname}
                      </div>
                      <div className="text-sm text-gray-600">{contact.email}</div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="p-3 text-center text-gray-500">No contacts found</div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="sticky bottom-0 flex items-center justify-between border-t border-gray-200 bg-gray-50 p-3">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiContactSearchDropdown;
